import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { API_ROUTES } from "../../routes/apiRoutes";
import { api } from "../api";
import { useTranslation } from "react-i18next";
import { useToast } from "../../components/shared/ToastProvider";

export const usePermissions = (platform_type) => {
  return useQuery({
    queryKey: ["permissions", platform_type],
    enabled: !!platform_type,
    queryFn: async () => {
      const res = await api.get(API_ROUTES.PERMISSIONS, { params: { platform_type } });
      return res?.data?.data;
    },
  });
};

export const useRolePermissions = (roleId) => {
  return useQuery({
    queryKey: ["role-permissions", roleId],
    queryFn: async () => {
      const res = await api.get(API_ROUTES.ROLE_PERMISSIONS(roleId));
      return res?.data?.data;
    },
    enabled: !!roleId,
  });
};

export const useUpdatePermissions = (roleId) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: (data) =>
      api.post(API_ROUTES.ROLE_PERMISSIONS(roleId), {
        roleId,
        permissions: data,
      }),
    onError: (error) => {
      const status = error?.response?.status;
      if (status !== 403 && status !== 409) {
        addToast(
          error?.response?.data?.message ?? t("rolesInSystem.updatePermissions.error"),
          "error",
        );
      }
    },
    onSuccess: () => {
      addToast(t("rolesInSystem.updatePermissions.success"), "success");
      queryClient.refetchQueries({ queryKey: ["user-permissions"] });
      queryClient.refetchQueries({ queryKey: ["role-permissions", roleId] });
    },
  });
};

export const useUserPermissions = () => {
  const { data: permissions, isLoading } = useQuery({
    queryKey: ["user-permissions"],
    queryFn: async () => {
      const res = await api.get(API_ROUTES.USER_PERMISSIONS);
      return res?.data?.data;
    },
  });

  const hasPermission = (permission) => {
    if (!permissions) return false;
    return permissions.includes(permission);
  };

  return { permissions, hasPermission, isLoading };
};
